// Type-to-search across every scheme AMFI publishes, not just the ones we rank.
//
// Schemes whose NAV history we do not hold are shown but not selectable, with the reason on the
// row. Letting someone pick one and then showing an error would be worse than saying so up front.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FundPicker({ value, label, onPick, placeholder = 'Search any fund…' }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const box = useRef(null);

  // Close when the click lands outside, the behaviour every other combobox has.
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const run = useCallback((text) => {
    if (text.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    fetch(`${API}/api/funds/search?q=${encodeURIComponent(text)}&limit=25`)
      .then((r) => r.json())
      .then((d) => setResults(d?.funds || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  // Debounced: a request per keystroke would hammer the API for no better answer.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => run(q), 250);
    return () => clearTimeout(t);
  }, [q, open, run]);

  const choose = (f) => {
    if (!f.has_history) return;
    onPick(f);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative" ref={box}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
                className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-left text-sm
                           text-gray-900 flex items-center gap-2 hover:border-gray-500 transition-colors">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="truncate">{label || placeholder}</span>
        </button>
      ) : (
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={placeholder}
                 className="w-full h-10 pl-9 pr-9 rounded-lg border border-emerald-500 bg-white text-sm text-gray-900
                            placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <button type="button" onClick={() => { setOpen(false); setQ(''); }}
                  aria-label="Close search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-gray-200
                        bg-white shadow-lg">
          {loading && <p className="px-3 py-3 text-sm text-gray-500">Searching…</p>}
          {!loading && q.trim().length < 2 && (
            <p className="px-3 py-3 text-sm text-gray-500">Type at least two characters, e.g. “sbi small”.</p>
          )}
          {!loading && q.trim().length >= 2 && results?.length === 0 && (
            <p className="px-3 py-3 text-sm text-gray-500">No scheme matches that.</p>
          )}
          {!loading && results?.map((f) => (
            <button key={f.code} type="button" onClick={() => choose(f)} disabled={!f.has_history}
                    className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-0 ${
                      f.has_history ? 'hover:bg-emerald-50 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
              <span className="flex items-center gap-2">
                {value === f.code && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                <span className="text-sm font-medium text-gray-900">{f.name}</span>
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">
                {[f.amc, f.plan, f.option].filter(Boolean).join(' · ')}
                {!f.has_history && ' · no NAV history held yet'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
